"use client";

import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type {
  ReelCreationSettingsByTemplate,
  TemplateType,
} from "@reelmagic/domain";
import { getCurrentUserProfile } from "@/lib/firestore/app-data";
import { reportCaughtError } from "@/lib/sentry/report-caught-error";
import { useFirebaseAuth } from "../auth/AuthProvider";

export function useTemplateCreationSettings<T extends TemplateType>({
  initialSettings,
  loadSavedSettings = true,
  resolveSavedSettings,
  templateType,
}: {
  initialSettings: ReelCreationSettingsByTemplate[T];
  loadSavedSettings?: boolean;
  resolveSavedSettings: (
    value: unknown,
    legacyMusicTrackId?: string,
  ) => ReelCreationSettingsByTemplate[T];
  templateType: T;
}): {
  isSettingsLoading: boolean;
  setSettings: Dispatch<
    SetStateAction<ReelCreationSettingsByTemplate[T]>
  >;
  settings: ReelCreationSettingsByTemplate[T];
} {
  const { user } = useFirebaseAuth();
  const [settings, setSettings] = useState(initialSettings);
  const [isSettingsLoading, setIsSettingsLoading] =
    useState(loadSavedSettings);

  useEffect(() => {
    if (!loadSavedSettings || !user?.uid) {
      return;
    }

    let isActive = true;
    void getCurrentUserProfile(user.uid)
      .then((profile) => {
        if (!isActive) {
          return;
        }

        setSettings(
          resolveSavedSettings(
            profile?.lastCreationSettingsByTemplate?.[templateType],
            profile?.lastMusicTrackIdByTemplate?.[templateType],
          ),
        );
      })
      .catch((error: unknown) => {
        reportCaughtError(error, {
          context: { templateType },
          operation: "create.load_last_creation_settings",
        });
      })
      .finally(() => {
        if (isActive) {
          setIsSettingsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [loadSavedSettings, resolveSavedSettings, templateType, user?.uid]);

  return {
    isSettingsLoading,
    setSettings,
    settings,
  };
}
